import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useParams } from 'react-router-dom'
import { z } from 'zod'
import { zodResolver } from '@hookform/resolvers/zod'
import { useCustomerInfo } from '../../../../app/hooks/useCustomerInfo'
import { formatPhoneNumber } from '../../../../app/utils/formatPhoneNumber'
import { formatZipCode } from '../../../../app/utils/formatZipCode'
import { formatAccountAndAgency } from '../../../../app/utils/formatAccountAndAgency'
import { banksResponse } from '../../../../app/services/transactionService/banks'
import { formatDateForInput } from '../../../../app/utils/formatDate'

const schema = z.object({
  name: z.string().min(1, 'Nome é obrigatório'),
  email: z.string().min(1, 'E-mail é obrigatório').email('E-mail inválido'),
  phone: z.string().min(1, 'Telefone é obrigatório'),
  birthDate: z.string().optional(),
  rg: z.object({
    date: z.string().optional(),
    number: z.string().optional(),
    org: z.string().optional(),
    uf: z.string().optional(),
  }),
  address: z.object({
    zipCode: z.string().optional(),
    state: z.string().optional(),
    city: z.string().optional(),
    neighborhood: z.string().optional(),
    street: z.string().optional(),
    number: z.string().optional(),
    complement: z.string().optional(),
  }),
  bankAccount: z.object({
    bank: z.string().optional(),
    agency: z.string().optional(),
    account: z.string().optional(),
    accountType: z.string().optional(),
    pixKey: z.string().optional(),
    pixType: z.string().optional(),
  }),
})

export type FormCustomerData = z.infer<typeof schema>

export const useCustomerDetailsPageController = () => {
  const { id } = useParams()
  const methods = useForm<FormCustomerData>({
    resolver: zodResolver(schema),
  })

  const getBank = (banks: banksResponse, bankNumber: string) => {
    if (!banks.banks) return
    const userBank = banks.banks.filter((bank) => bank.value === bankNumber)

    if (userBank.length > 0) {
      return userBank[0].label
    } else {
      return bankNumber
    }
  }

  const { data, isFetching, isLoading } = useCustomerInfo(id!)

  const { reset } = methods

  const safeFormatDate = (date?: string) => {
    if (!date) return ''
    const parsed = new Date(date)
    return isNaN(parsed.getTime()) ? '' : formatDateForInput(date)
  }

  useEffect(() => {
    if (data) {
      reset({
        ...data,
        birthDate: safeFormatDate(data.birthDate),
        phone: data.phone ? formatPhoneNumber(data.phone) : '',
        address: {
          ...data.address,
          zipCode: data.address?.zipCode
            ? formatZipCode(data.address.zipCode)
            : '',
        },
        rg: {
          ...data.rg,
          date: data?.rg?.date ? formatDateForInput(data?.rg?.date) : '',
        },
        bankAccount: {
          ...data.bankAccount,
          account: data.bankAccount?.account
            ? formatAccountAndAgency(data.bankAccount.account)
            : '',
        },
      })
    }
  }, [reset, data])

  return {
    methods,
    data,
    isFetching,
    isLoading,
    getBank,
  }
}
